<template>
  <div class="cart__items">
    <!-- Tạm thời tắt progress bar của chiến dịch Buy more save more -->
    <!-- <CartDiscountProgressBar class="pb-xs" /> -->
    <ProductFreeShipProgress v-if="cartStore.totalCartItems" class="pb-xs" />
    <div class="cart__items-header">
      <span> Added Items </span>
      <div class="cart__items-header-quantity-container">
        <span></span>
        <div class="cart__items-header-quantity">
          <span>Quantity</span>
          <span></span>
        </div>
      </div>
    </div>
    <template v-if="!loading && !cartLines.length">
      <div class="cart__no-item mb-sm">
        <span>You have no items in your cart!</span>
      </div>
      <!-- <CartRecoveryEmail
        @cart-recovery="$emit('cart-recovery')"
        class="mb-sm"
      /> -->
    </template>
    <template v-else>
      <CartItem
        v-for="(item, index) of loading ? placeholderItems : cartLines"
        :key="item?.id || index"
        @update-quantity="updateQuantity"
        @delete-item="deleteCartItem"
        @link-clicked="$emit('link-clicked')"
        :item="item"
        v-show="isShowItem(item) || loading"
      ></CartItem>
    </template>
    <GreetingCardBox
      v-if="
        isGreetingCardFlow &&
        !isProductTypeGreetingCardInLineItems &&
        cartLines.length
      "
      class="mt-sm"
    />

    <ConfirmationPopup
      group="cart-delete-item-confirmation"
      content-id="cart-delete-item-confirmation__content"
      ref="cartDeleteItemConfirmationPopup"
    ></ConfirmationPopup>
  </div>
</template>

<script lang="ts" setup>
import useCartStore from '@/store/cart'
import { computed, Ref } from '@vue/reactivity'
import {
  CURRENCY,
  debounce,
  EVENT_TRACKING_PRODUCT_REMOVED,
  getCurrentTimeStamp,
  PRODUCT_TYPE_GREETING_CARD,
  PRODUCT_TYPE_QR_GREETING_CARD,
  MADEMINE_ATTRIBUTE_KEY,
  HANDLE_PRODUCT_UPSELL_WITH_GREETING_CARD,
  TIME_ATC_UPSELL_WITH_GREETING_CARD,
} from '@/utils'
import { CartLineItem } from '@/types'
import CartItem from './CartItem.vue'
import CartDiscountProgressBar from './CartDiscountProgressBar.vue'
import ProductFreeShipProgress from '@/components/product/ProductFreeShipProgress.vue'
import GreetingCardBox from '@/components/product/GreetingCardBox.vue'
import Analytics from '@/services/analytics'
import {
  buildProductUrl,
  getRawIdFromShopifyId,
  isProductHasType,
  isProductHasTypeStartWith,
  standardizeCartIdFromShopifyId,
} from '@/utils/product'
import ConfirmationPopup from '@/ui-kits/ConfirmationPopup.vue'
import { onMounted, ref } from 'vue'
import useDiscountStore from '@/store/discount'
import useMediaCampaignStore from '@/store/media'
import { useQrGreetingCardAndDigitalContent } from '@/pages/product/composables/useQrGreetingCardAndDigitalContent'
import { useRoute } from 'vue-router'
import Logger from '@/services/log'

const route = useRoute()

const mediaCampaignStore = useMediaCampaignStore()

const isGreetingCardFlow = computed(() => mediaCampaignStore.greetingCardFlow)

const isProductTypeGreetingCardInLineItems = computed(() => {
  const index = cartStore.localCartLineItems.findIndex((lineItem) =>
    isProductHasType(lineItem?.merchandise?.product, [
      PRODUCT_TYPE_GREETING_CARD,
    ])
  )
  return index != -1
})

defineProps<{ loading: boolean }>()
const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
  (e: 'link-clicked'): void
  (e: 'cart-recovery'): void
}>()

const { checkQrGreetingaCardAndDigitalContent } =
  useQrGreetingCardAndDigitalContent()
const cartStore = useCartStore()
const discountStore = useDiscountStore()
const cartLines = computed(() => cartStore.localCartLineItems || [])
const placeholderItems: CartLineItem[] = Array(3).fill(undefined)
const cartDeleteItemConfirmationPopup: Ref<typeof ConfirmationPopup | null> =
  ref(null)
const isBOGOCampaign = computed(() => {
  return discountStore.isBOGOCampaign
})

const isCartPage = computed(() => route.name == 'cart')

function isShowItem(lineItem: any) {
  if (!lineItem) return
  if (
    !isProductHasType(lineItem.merchandise.product, [
      PRODUCT_TYPE_QR_GREETING_CARD,
    ]) &&
    !isProductHasTypeStartWith(lineItem.merchandise.product, 'digital')
  )
    return true

  const lineItemsQrGreetingCardAndDigitalContent =
    checkQrGreetingaCardAndDigitalContent()
  const lineItemsQrGreetingCardAndDigitalContentNoHidden =
    lineItemsQrGreetingCardAndDigitalContent.filter((item) => !item.isHidden)
  return !!lineItemsQrGreetingCardAndDigitalContentNoHidden.find(
    (item) => item.lineItemId == lineItem.id
  )
}

onMounted(async () => {
  if (isCartPage.value) {
    const lineItemsQrGreetingCardAndDigitalContent =
      checkQrGreetingaCardAndDigitalContent()
    if (lineItemsQrGreetingCardAndDigitalContent.length) {
      const lineItemsNeedRemove =
        lineItemsQrGreetingCardAndDigitalContent.filter((item) => item.isDelete)
      if (lineItemsNeedRemove.length) {
        for (let i = 0; i < lineItemsNeedRemove.length; i++) {
          await updateCart(0, lineItemsNeedRemove[i].lineItemId)
        }
      }
    }
  }
})

async function updateQuantity(typeInput: string, idInput: string) {
  let id = idInput
  let type = typeInput
  let lineItem = cartLines.value.find((item) => item.id === id)

  if (!lineItem) return

  let quantityChanged = 0
  switch (type) {
    case 'plus':
      quantityChanged = 1
      break
    case 'minus':
      quantityChanged = -1
      break
    case 'delete':
      quantityChanged = -lineItem.quantity
  }

  if (quantityChanged == 0) return
  // decide the type of action based on the original line item
  if (lineItem.quantity + quantityChanged == 0) {
    type = 'delete'
  }

  // BOGO campaign currently has bug with removing lineItems with same variant ID
  // so we need special logic to cover it
  if (isBOGOCampaign.value) {
    // Check if the updating item is the item that got discounted
    if (lineItem.price === 0) {
      // Find if there are any line item that has same variant id with the updating line item
      // Make the update for that item because the API don't let we update for the line item that got discounted
      const sameVariantIdLineItem = cartLines.value.find(
        (item) => item.merchandise.id === lineItem?.merchandise.id
      )
      // switch to update the lineitem with same variant id but do not got discounted
      if (sameVariantIdLineItem) {
        lineItem = sameVariantIdLineItem
        id = lineItem.id
      }
    }
  }

  let newQuantity = lineItem.quantity + quantityChanged
  if (newQuantity < 0) newQuantity = 0

  let debounceFn = debounces[id]

  if (!debounceFn) {
    debounceFn = debounces[id] = debounce(updateCart, 300, true)
  }

  // show confirmation popup for deleting case
  if (type === 'delete') {
    const confirm = await confirmDelete()

    if (!confirm) {
      // do nothing in case cancel deleting action
      return
    }

    // Need to re-assign the lineItem here
    // because in some situation, the cartLineItems is updated with new value from API response
    lineItem = cartLines.value.find((item) => item.id === id) || lineItem
  }

  // set the line item quantity to new quantity to update the UI
  lineItem.quantity = newQuantity

  // Do not debounce the execution in case BOGO campaign and deleting item
  // because the number of items will change
  const shouldUseDebounce = !isBOGOCampaign.value && newQuantity != 0

  if (!shouldUseDebounce) {
    emit('update:loading', true)
    if (debounceFn) {
      debounceFn.clear()
    }
    await updateCart(newQuantity, lineItem.id, lineItem)
    if (type === 'delete') {
      // remove item up sell with line item base on attribute
      const ids = getLineUpsellIds(lineItem)
      if (ids?.length) {
        for (let i = 0; i < ids.length; i++) {
          await updateCart(0, ids[i])
        }
      }
      trackDeletingCartItem(lineItem)
    }
    emit('update:loading', false)
    return
  }

  // update the last update timestamp to tell the store that there are some incoming update are waiting
  // so it should not update the line item with the result from the server
  cartStore.lastUpdateTimeStamp = getCurrentTimeStamp()
  debounceFn(newQuantity, lineItem.id, lineItem)
}

const debounces: {
  [key: string]: ReturnType<typeof debounce>
} = {}

async function updateCart(
  quantity: number,
  id: string,
  lineItem?: CartLineItem
) {
  try {
    // synchronize cart online store and shopify theme store
    if (lineItem) {
      const mademineKeyItemAttribute = lineItem.attributes?.find(
        (attribute) => attribute.key == MADEMINE_ATTRIBUTE_KEY
      )
      if (mademineKeyItemAttribute?.value) {
        // const provider = await Provider.getInstance()
        // await provider.updateCartLineItemShopifyTheme(
        //   mademineKeyItemAttribute.value,
        //   quantity
        // )
      }
    }
  } catch (err) {
    Logger.error('Error when update cart line item shopify theme', err)
  }

  const timeStamp = getCurrentTimeStamp()
  return cartStore.updateCartLine({ id, quantity, timeStamp })
}

async function deleteCartItem(id: string) {
  return updateQuantity('delete', id)
}

function getLineUpsellIds(lineItem: CartLineItem) {
  if (!lineItem) return
  const lineItemHandle = lineItem?.merchandise?.product?.handle
  const lineItemTimeStampATCAttributes = lineItem?.attributes.find(
    (attribute) => attribute.key == TIME_ATC_UPSELL_WITH_GREETING_CARD
  )
  const linesUpsell = cartStore.cart.lines.filter((line) => {
    const lineAttributes = line.attributes
    if (!lineAttributes?.length) return false
    const handleProductUpsellWithGreetingCardAttribute = lineAttributes.find(
      (attribute) => attribute.key == HANDLE_PRODUCT_UPSELL_WITH_GREETING_CARD
    )

    const timeAddToCartUpsellWithGreetingCard = lineAttributes.find(
      (attribute) => attribute.key == TIME_ATC_UPSELL_WITH_GREETING_CARD
    )
    if (
      timeAddToCartUpsellWithGreetingCard &&
      lineItemTimeStampATCAttributes &&
      handleProductUpsellWithGreetingCardAttribute &&
      handleProductUpsellWithGreetingCardAttribute?.value == lineItemHandle &&
      timeAddToCartUpsellWithGreetingCard?.value ==
        lineItemTimeStampATCAttributes?.value
    ) {
      return true
    }
    return false
  })
  if (!linesUpsell.length) return
  const lineUpselIds = linesUpsell.map((line) => line.id)
  return lineUpselIds
}

async function trackDeletingCartItem(lineItem: CartLineItem) {
  if (lineItem) {
    const product = lineItem.merchandise.product
    const item = {
      product_id: lineItem.merchandise.sku,
      sku: lineItem.merchandise.sku,
      name: product?.title,
      merchandise: getRawIdFromShopifyId(lineItem.merchandise.id),
      price: lineItem.merchandise.price,
      quantity: lineItem.quantity,
      handle: product?.handle,
      url: window.location.origin + buildProductUrl(product?.handle || ''),
      image_url: product?.featuredImage?.src,
      type: product?.productType,
      tags: product?.tags,
      collections: product?.collections?.map((collection) => collection.title),
    }
    Analytics.track(EVENT_TRACKING_PRODUCT_REMOVED, {
      ...item,
      cart_id: standardizeCartIdFromShopifyId(cartStore.cart?.id),
      currency: CURRENCY,
      products: [item],
    })
  }
}

async function confirmDelete() {
  try {
    if (!cartDeleteItemConfirmationPopup.value) return false
    return await cartDeleteItemConfirmationPopup.value.confirm()
  } catch (error) {
    return false
  }
}
</script>

<style lang="scss">
.cart {
  $S: &;

  &__items {
    grid-area: a;
    padding-top: 1em;

    &-header {
      display: none;
    }
  }

  @include media-desktop {
    &--full-page {
      #{$S} {
        &__items {
          &-header {
            display: grid;
            grid-template-columns: 135px 1fr;
            gap: 1.5em;
            border-bottom: solid 1px var(--border-color);
            font-weight: 600;
            padding-bottom: 0.5em;
            margin-bottom: 1.5em;

            &-quantity-container {
              display: grid;
              grid-template-columns: 5fr 3fr;
            }

            &-quantity {
              display: flex;
              justify-content: space-evenly;

              span {
                min-width: 2em;
              }
            }
          }
        }
      }
    }
  }
}
</style>
